import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  FrontendLanguage,
  SUPPORTED_LANGS,
  SUPPORTED_LANGS_WITHOUT_SWISS_GERMAN,
  MAIN_LANGS,
  FRONTEND_LANGUAGE_LABELS,
} from '../../core/utils/languageUtils';
import { Observable, withLatestFrom } from 'rxjs';
import { LanguageService } from '../../core/services/language/language.service';
import { Store } from '@ngrx/store';
import { getAddressOrUndefined } from '../../core/states/offer/offer.selector';
import { first, map, tap } from 'rxjs/operators';
import { AddressState } from '../../core/states/offer/offer.state';
import { getBerneseSpokenZipCodeAreal } from './bernese-german-spoken-zip-list';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrl: './language-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelectorComponent implements OnInit {
  protected readonly FRONTEND_LANGUAGE_LABELS = FRONTEND_LANGUAGE_LABELS;

  public supportedLanguages$: Observable<FrontendLanguage[]>;
  public mainLanguages$: Observable<FrontendLanguage[]>;
  public currentLanguage$: Observable<FrontendLanguage>;
  public additionalLanguages$: Observable<FrontendLanguage[]>;

  public isOpen: boolean = false;
  @Input() public displayInHeader: boolean | false;

  constructor(
    private languageService: LanguageService,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store,
  ) {}

  public ngOnInit(): void {
    this.currentLanguage$ = this.languageService.getCurrentLanguage$();
    this.supportedLanguages$ = this.store.select(getAddressOrUndefined).pipe(
      withLatestFrom(this.currentLanguage$),
      tap(([addressOrUndefined, currentLanguage]) =>
        this.resetLanguageIfAddressIsNotBerneseAndCurrentLanguageIsSwissGerman(addressOrUndefined, currentLanguage),
      ),
      map(([addressOrUndefined, currentLanguage]) => {
        if (addressOrUndefined === undefined) {
          return currentLanguage === 'ch' ? SUPPORTED_LANGS : SUPPORTED_LANGS_WITHOUT_SWISS_GERMAN;
        }

        return this.isBerneseSpokenZipCode(Number(addressOrUndefined.zipCode))
          ? SUPPORTED_LANGS
          : SUPPORTED_LANGS_WITHOUT_SWISS_GERMAN;
      }),
    );
    this.mainLanguages$ = this.supportedLanguages$.pipe(
      map((languages) => {
        return languages.filter((language) => MAIN_LANGS.includes(language));
      }),
    );
    this.additionalLanguages$ = this.supportedLanguages$.pipe(
      map((languages) => {
        return languages.filter((language) => !MAIN_LANGS.includes(language));
      }),
    );
  }

  private resetLanguageIfAddressIsNotBerneseAndCurrentLanguageIsSwissGerman(
    addressStateOrUndefined: AddressState | undefined,
    currentLanguage: FrontendLanguage,
  ): void {
    if (addressStateOrUndefined === undefined) return;

    if (!this.isBerneseSpokenZipCode(Number(addressStateOrUndefined.zipCode)) && currentLanguage === 'ch') {
      this.changeLanguage('de');
    }
  }

  public changeLanguage(language: FrontendLanguage): void {
    this.languageService
      .setLanguage$(language)
      .pipe(first(), untilDestroyed(this))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  private isBerneseSpokenZipCode(zipCode: number): boolean {
    return getBerneseSpokenZipCodeAreal.some((item) => zipCode >= item.start && zipCode <= item.end);
  }
}
